import React, { createContext, useContext, useState } from "react"
import { Lock, Unlock } from "lucide-react"
import { REPORT_COLORS, REPORT_RADIUS } from "./reportTheme"

// The admin can gate any field in the shared report from the admin panel.
// A gated field arrives in place of its normal value as:
//   { blurred: true, message, label, value_type }
// This can show up at ANY depth — a top-level stat, a single array item, an
// entire array/section — so every place that reads a raw API value should
// route it through here first.
export const isBlurredField = value => !!value && typeof value === "object" && value.blurred === true

// Splits a list into real items and admin-locked ones — the single place
// every "some items in this list are individually blurred" call site should
// go through, so a section's locked items always consolidate into ONE
// unlock affordance instead of one per locked item, regardless of how many
// there are or where they fell in the original array.
export const partitionBlurred = items => {
  const list = Array.isArray(items) ? items : items == null ? [] : [items]
  const visible = list.filter(item => !isBlurredField(item))
  const locked = list.filter(isBlurredField)
  return { visible, locked, hasLocked: locked.length > 0 }
}

// Every locked field — logged in, guest, or fully anonymous — calls the same
// `openUpgradeModal` from this context. The provider (PublicReport's
// requestUnlock) decides what that means: an in-place sign in / sign up
// modal for a logged-out or guest visitor, the shared plans modal
// (AppsTrackingPlanModal) for a signed-in one — so nothing here needs to
// know or branch on login state itself.
export const BlurUpgradeContext = createContext({ openUpgradeModal: () => {} })

// Fake but plausibly-shaped placeholder content, blurred via CSS filter
// rather than rendered as a flat gray bar — the goal is "real data sits here,
// obscured," not "this is still loading."
export const PLACEHOLDER_WORD = "Sample data"
const PLACEHOLDER_STAT = "12,480"
export const PLACEHOLDER_SENTENCE = "Detailed analysis based on this app's underlying performance data is available on a paid plan."
export const PLACEHOLDER_TAGS = ["keyword", "search term", "phrase", "topic", "query", "tag"]

// A column/list that hides every one of its items when they're all locked
// can end up completely empty above the section's one shared Unlock button
// — reading as "there's nothing here" rather than "this is gated". Drop
// this in wherever that happens: a couple of deliberately blurred filler
// lines so the reader still sees "there's content, it's just locked",
// without duplicating the section's own CTA (this renders no button).
export function BlurredStaticLines({ lines = 2 }) {
  return (
    <div aria-hidden="true" style={{ userSelect: "none", pointerEvents: "none" }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          style={{
            fontSize: 12,
            color: REPORT_COLORS.textSecondary,
            filter: `blur(${4 + i * 2}px)`,
            opacity: Math.max(0.35, 0.75 - i * 0.15),
            marginBottom: i < lines - 1 ? 8 : 0,
          }}
        >
          {PLACEHOLDER_SENTENCE}
        </div>
      ))}
    </div>
  )
}

// Stand-in for a single value. `size` controls how much space the
// placeholder takes up, so the blur reads as proportional to what's actually
// hidden instead of every field — a number, a badge, or a full paragraph of
// AI analysis — collapsing to the same tiny pill:
//   "sm" (default) — a short label or tag.
//   "stat" — a headline number (a stat tile value, a big estimate figure) —
//            a wider solid bar sized to match that bold/oversized font
//            instead of the sm pill looking lost next to it.
//   "lg" — a full sentence or paragraph (description, summary, review body,
//          a suggested-fix write-up) — rendered as a multi-line text skeleton
//          so its footprint hints at how much content is locked.
// `interactive` (default true) controls whether this renders its own click
// target. A section can have several blurred fields but must only ever
// offer ONE way to unlock it — when a section already shows one consolidated
// CTA elsewhere (a BlurredContentFade/BlurredGridPreview, or another field in
// the same row), every other blurred field in that section should pass
// interactive={false}: still visibly blurred, but a plain non-clickable
// <span> with no "Unlock" cue, so the reader isn't offered N buttons that
// all do the exact same thing.
export function BlurredInline({ field, size = "sm", interactive = true }) {
  const { openUpgradeModal } = useContext(BlurUpgradeContext)
  const [hovered, setHovered] = useState(false)
  const message = (field && field.message) || "More detail is available on the full report."
  const isLarge = size === "lg"
  const isStat = size === "stat"
  const isXs = size === "xs"

  // A soft solid border over a faint gradient reads as a deliberate premium
  // panel; the previous dashed-red-outline treatment read as a warning box,
  // which is the wrong register for an upsell. The subtle hover lift/glow is
  // the only cue that needs motion — it hints "clickable" without the
  // shimmering/animated placeholder text implying something is still loading.
  // Non-interactive fields skip the border/hover chrome entirely — no box,
  // no lift, just the blurred text sitting inline — since they're not a
  // click target and shouldn't look like one next to the section's one real
  // CTA.
  const baseStyle = interactive
    ? {
        border: `1px solid rgba(246,37,90,${hovered ? 0.4 : 0.22})`,
        boxShadow: hovered ? "0 4px 14px rgba(246,37,90,0.14)" : "none",
        transform: hovered ? "translateY(-1px)" : "none",
        transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
        cursor: "pointer",
      }
    : { border: "none", background: "none", padding: 0, cursor: "default" }

  // baseStyle spreads LAST in each branch so a non-interactive field's
  // border:none/background:none/padding:0 actually win over the size
  // defaults below, instead of being overwritten by them.
  const style = isLarge
    ? {
        display: "flex",
        flexDirection: "column",
        gap: 6,
        width: "100%",
        maxWidth: 420,
        padding: "10px 12px",
        borderRadius: 12,
        background: `linear-gradient(160deg, ${REPORT_COLORS.accentSoft}, rgba(255,255,255,0.02))`,
        color: REPORT_COLORS.textMuted,
        textDecoration: "none",
        fontFamily: "inherit",
        textAlign: "left",
        ...baseStyle,
      }
    : isStat
      ? {
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 14px",
          borderRadius: 8,
          background: `linear-gradient(160deg, ${REPORT_COLORS.accentSoft}, rgba(255,255,255,0.02))`,
          color: REPORT_COLORS.textMuted,
          textDecoration: "none",
          fontFamily: "inherit",
          whiteSpace: "nowrap",
          ...baseStyle,
        }
      : isXs
        ? {
            // Same readable sizing as "sm" — the width problem was the
            // "Sample data" placeholder word (11 characters), not the icon
            // or text size, so xs only drops that word rather than shrinking
            // everything down to illegible.
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            borderRadius: 999,
            background: `linear-gradient(160deg, ${REPORT_COLORS.accentSoft}, rgba(255,255,255,0.02))`,
            color: REPORT_COLORS.textMuted,
            textDecoration: "none",
            fontSize: "0.9em",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            ...baseStyle,
          }
        : {
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            borderRadius: 999,
            background: `linear-gradient(160deg, ${REPORT_COLORS.accentSoft}, rgba(255,255,255,0.02))`,
            color: REPORT_COLORS.textMuted,
            textDecoration: "none",
            fontSize: "0.9em",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            ...baseStyle,
          }

  // The placeholder should read as real content that's obscured — actual
  // letterforms under a blur filter — not a flat loading-skeleton bar (no
  // shimmer/animation, since nothing is "loading"; it's deliberately hidden).
  // Every variant also carries a plain-language "Unlock" cue in the accent
  // color, on top of cursor:pointer, so the click affordance doesn't rely on
  // a visitor guessing or hovering for the tooltip. The "lg" copy is kept to
  // one icon + the admin's own message + a small Unlock chip — the earlier
  // version also repeated a generic "Locked content / Click to unlock"
  // header AND a separate fake placeholder sentence, which read as filler
  // when this renders many times down one page.
  const content = isLarge ? (
    <>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, lineHeight: 1.4 }}>
          <Lock size={12} color={REPORT_COLORS.accent} style={{ flexShrink: 0 }} /> {message}
        </span>
        {interactive && (
          <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: REPORT_COLORS.accent, flexShrink: 0 }}>
            <Unlock size={11} /> Unlock
          </span>
        )}
      </span>
    </>
  ) : isStat ? (
    <>
      <Lock size={13} color={REPORT_COLORS.accent} />
      <span style={{ filter: "blur(4px)", opacity: 0.85, userSelect: "none", fontSize: 15, fontWeight: 700 }} aria-hidden="true">
        {PLACEHOLDER_STAT}
      </span>
      {interactive && <span style={{ fontSize: 10, fontWeight: 700, color: REPORT_COLORS.accent, whiteSpace: "nowrap" }}>Unlock</span>}
    </>
  ) : isXs ? (
    // No fake placeholder word here — at this size (as little as ~50-70px of
    // available width, e.g. a compact stat tile) even the shortest blurred
    // word plus "Unlock" doesn't fit. Icon + "Unlock" alone still reads as
    // "locked, click to see it" without needing the extra text. Icon/text
    // sizing matches "sm" — only the placeholder word is dropped.
    <>
      <Lock size={10} color={REPORT_COLORS.accent} />
      {interactive && <span style={{ fontSize: "0.85em", fontWeight: 700, whiteSpace: "nowrap" }}>Unlock</span>}
    </>
  ) : (
    <>
      <Lock size={10} color={REPORT_COLORS.accent} />
      <span style={{ filter: "blur(4px)", opacity: 0.85, userSelect: "none" }} aria-hidden="true">
        {PLACEHOLDER_WORD}
      </span>
      {interactive && <span style={{ fontSize: "0.85em", fontWeight: 700, color: REPORT_COLORS.accent }}>Unlock</span>}
    </>
  )

  if (!interactive) {
    return (
      <span title={message} style={style}>
        {content}
      </span>
    )
  }

  const hoverProps = { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }

  // Always the one shared Unlock entry point, logged in or not — the
  // context provider (see BlurUpgradeContext above) decides whether that's
  // sign-in first or straight to the plans modal, not this click site.
  return (
    <button type="button" onClick={openUpgradeModal} title={message} style={style} {...hoverProps}>
      {content}
    </button>
  )
}

// Pull a count out of the admin's message when they included one ("12 more
// keywords…") so the placeholder pill count is at least a plausible echo of
// reality; fall back to a small fixed number when they didn't. This is a
// visual cue, not a literal count — a whole-list lock never tells us the
// real one.
const guessCount = (message, fallback = 4) => {
  const match = typeof message === "string" ? message.match(/\d+/) : null
  const n = match ? parseInt(match[0], 10) : fallback
  return Math.min(Math.max(n, 2), 8)
}

const BADGE_TONE_COLORS = {
  good: [REPORT_COLORS.good, REPORT_COLORS.goodSoft],
  warning: [REPORT_COLORS.warning, REPORT_COLORS.warningSoft],
  serious: [REPORT_COLORS.serious, REPORT_COLORS.seriousSoft],
  critical: [REPORT_COLORS.critical, REPORT_COLORS.criticalSoft],
  neutral: [REPORT_COLORS.textSecondary, "rgba(255,255,255,0.06)"],
}

// Shared by BlurredBadgeList and BlurredTagGroup: a clickable row of
// blurred-tag pills (matching ReportBadge's own shape/tone) ending in one
// "Unlock" cue — a single click target for the whole group instead of a
// separate lock+unlock chip per item. Pass interactive=false when a sibling
// row in the same card already carries the section's one shared Unlock —
// this row then renders as plain (non-clickable) blurred pills with no
// "Unlock" cue of its own.
const LockedPillRow = ({ count, tone, message, extraLabel, interactive = true }) => {
  const { openUpgradeModal } = useContext(BlurUpgradeContext)
  const [color, bg] = BADGE_TONE_COLORS[tone] || BADGE_TONE_COLORS.neutral
  // Always the shared upgrade modal — see the matching comment in
  // BlurredInline above.
  const Wrapper = interactive ? "button" : "span"
  const wrapperProps = !interactive ? {} : { type: "button", onClick: openUpgradeModal }

  return (
    <Wrapper
      {...wrapperProps}
      title={message}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        cursor: interactive ? "pointer" : "default",
        fontFamily: "inherit",
        textDecoration: "none",
      }}
    >
      <Lock size={12} color={REPORT_COLORS.accent} style={{ marginRight: 2 }} />
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            color,
            backgroundColor: bg,
            border: `1px solid rgba(246,37,90,0.25)`,
          }}
        >
          <span style={{ filter: `blur(${3 + i * 1.2}px)`, opacity: Math.max(0.5, 0.9 - i * 0.08), userSelect: "none" }} aria-hidden="true">
            {PLACEHOLDER_TAGS[i % PLACEHOLDER_TAGS.length]}
          </span>
        </span>
      ))}
      {interactive && (
        <span style={{ fontSize: 11, fontWeight: 700, color: REPORT_COLORS.accent, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
          <Unlock size={11} /> {extraLabel || "Unlock"}
        </span>
      )}
    </Wrapper>
  )
}

// Stand-in for a whole locked LIST of tag-shaped items (keywords, platforms,
// competitor names) that the admin gated as one descriptor rather than
// per-item. Renders as a row of pills matching ReportBadge's own look —
// same shape/tone as the real tags it replaces — each containing blurred
// placeholder text, so a hidden list of several keywords reads as "several
// keywords," not one generic lock icon. The pill count is guessed from the
// admin's message (or a small default) since a whole-list lock never tells
// us the real number.
export function BlurredBadgeList({ field, tone = "neutral", interactive = true }) {
  const message = (field && field.message) || "This data is not available in the shared report."
  return <LockedPillRow count={guessCount(message)} tone={tone} message={message} interactive={interactive} />
}

// Stand-in for a MIXED array of tag-shaped items where only SOME entries are
// individually locked (each carrying its own {blurred:true,...} descriptor)
// — e.g. Target Keywords, where a few keywords are gated but the rest are
// visible. Rather than each locked keyword getting its own separate
// lock+Unlock chip scattered inline among the visible ones, the visible tags
// render first and every locked one is grouped onto its own row below with a
// single shared blur + one "Unlock" affordance — using the REAL count of
// locked items (we know exactly which array entries are gated here, unlike
// a whole-list lock), not a guess.
export function BlurredTagGroup({ items, tone = "neutral", renderVisible }) {
  const list = Array.isArray(items) ? items : items == null ? [] : [items]
  const visible = list.filter(item => !isBlurredField(item))
  const blurred = list.filter(isBlurredField)
  const [color, bg] = BADGE_TONE_COLORS[tone] || BADGE_TONE_COLORS.neutral

  if (!visible.length && !blurred.length) return null

  return (
    <>
      {visible.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: blurred.length > 0 ? 8 : 0 }}>
          {visible.map(renderVisible)}
        </div>
      )}
      {blurred.length > 0 && (
        <BlurredContentFade
          // Preview only a handful of pills, however many are actually
          // locked — 26 locked keywords wrapped as 26 fading pills is its
          // own kind of clutter; a few is enough to read as "there's more
          // here," with the CTA label naming the real total.
          items={blurred.slice(0, 6)}
          renderItem={(_, i) => (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                color,
                backgroundColor: bg,
                border: `1px solid rgba(246,37,90,0.25)`,
              }}
            >
              {PLACEHOLDER_TAGS[i % PLACEHOLDER_TAGS.length]}
            </span>
          )}
          wrap
        />
      )}
    </>
  )
}

// Stand-in for "there's more below, but it's gated" — a fixed-height preview
// of the next few list items (reviews, feed entries, etc.), each rendered
// for real via the caller's own row renderer (so an individually-locked item
// still gets its normal blur treatment underneath) but blurred a little more
// than the previous one, clipped and faded into the surrounding background,
// with ONE full-width "Unlock" button — instead of a per-item lock chip or a
// plain "show more" toggle. `items` may be empty — e.g. when the only thing
// gated is a whole OTHER section with nothing of its own to preview — in
// which case this collapses to a plain, compact CTA panel (optionally with
// a `subtitle` line naming everything the upgrade unlocks) instead of an
// empty blurred stack.
export function BlurredContentFade({ items = [], renderItem, ctaLabel = "Unlock full estimates", subtitle, maxHeight = 200, dense = false, wrap = false, hideCta = false }) {
  const { openUpgradeModal } = useContext(BlurUpgradeContext)
  const [hovered, setHovered] = useState(false)
  const hasPreview = items.length > 0
  // Always the shared upgrade modal — see the matching comment in
  // BlurredInline above.
  const Wrapper = "button"
  const wrapperProps = { type: "button", onClick: openUpgradeModal }

  // `wrap` is for a row of small tag/pill-shaped items (Target Keywords,
  // badge lists) — the default preview below stacks each item in its own
  // full-width block div, which is right for paragraph-shaped content
  // (reviews, feed entries) but wraps every locked PILL onto its own line,
  // leaving a tall, sparse-looking gap before the CTA. Here the locked pills
  // flow in the same wrapped row the visible tags already use, sized to
  // their own content, with the CTA sitting directly beneath — no absolute
  // overlay, no gradient scrim, no dead space.
  if (wrap) {
    return (
      <div style={{ marginTop: hasPreview ? 8 : 4 }}>
        {hasPreview && (
          <div aria-hidden="true" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, userSelect: "none", pointerEvents: "none" }}>
            {items.map((item, i) => (
              <div key={i} style={{ filter: `blur(${2 + i * 1.1}px)`, opacity: Math.max(0.35, 0.95 - i * 0.1) }}>
                {renderItem(item, i)}
              </div>
            ))}
          </div>
        )}
        <Wrapper
          {...wrapperProps}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            backgroundColor: REPORT_COLORS.accent,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: "nowrap",
            border: "none",
            cursor: "pointer",
            textDecoration: "none",
            fontFamily: "inherit",
            boxShadow: hovered ? "0 4px 14px rgba(246,37,90,0.3)" : "none",
            transform: hovered ? "translateY(-1px)" : "none",
            transition: "box-shadow 0.18s ease, transform 0.18s ease",
          }}
        >
          <Unlock size={13} /> {ctaLabel}
        </Wrapper>
      </div>
    )
  }

  // `dense` is the quiet, single-line shape for the many short stacked-text
  // sections (Blocking Issues, Platform Playbook, Suggestions, …) that each
  // just need ONE trailing "N more are locked" line once every real item in
  // the list has already been shown — a full centered CTA panel repeated
  // 8-10 times down one page reads as another slab, not a deliberate upsell.
  // Only meaningful with no preview stack (dense sections have nothing left
  // to fade — every real item already rendered above it by the caller).
  if (dense && !hasPreview) {
    return (
      <Wrapper
        {...wrapperProps}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          width: "100%",
          marginTop: 4,
          padding: "10px 14px",
          borderRadius: 10,
          background: `linear-gradient(160deg, ${REPORT_COLORS.accentSoft}, rgba(255,255,255,0.02))`,
          border: `1px solid rgba(246,37,90,${hovered ? 0.4 : 0.22})`,
          boxShadow: hovered ? "0 4px 14px rgba(246,37,90,0.14)" : "none",
          cursor: "pointer",
          textDecoration: "none",
          fontFamily: "inherit",
          transition: "border-color 0.18s ease, box-shadow 0.18s ease",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: REPORT_COLORS.textMuted }}>
          <Lock size={12} color={REPORT_COLORS.accent} /> {ctaLabel}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: REPORT_COLORS.accent, flexShrink: 0 }}>
          <Unlock size={11} /> Unlock
        </span>
      </Wrapper>
    )
  }

  // `hideCta`: still shows the faded preview stack, but skips the CTA
  // link/button — for when a parent already renders ONE shared Unlock below
  // several of these (e.g. the paired Download/Revenue estimate cards),
  // so each card doesn't repeat the same button.
  if (hideCta) {
    return (
      <div aria-hidden="true" style={{ userSelect: "none", pointerEvents: "none" }}>
        {items.map((item, i) => (
          <div key={i} style={{ filter: `blur(${2 + i * 2.5}px)`, opacity: Math.max(0.3, 1 - i * 0.22) }}>
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ position: "relative", maxHeight: hasPreview ? maxHeight : undefined, overflow: "hidden", borderRadius: 10, marginTop: 4 }}>
      {hasPreview && (
        <div aria-hidden="true" style={{ userSelect: "none", pointerEvents: "none" }}>
          {items.map((item, i) => (
            <div key={i} style={{ filter: `blur(${2 + i * 2.5}px)`, opacity: Math.max(0.3, 1 - i * 0.22) }}>
              {renderItem(item, i)}
            </div>
          ))}
        </div>
      )}
      <Wrapper
        {...wrapperProps}
        style={{
          position: hasPreview ? "absolute" : "static",
          inset: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: hasPreview ? "flex-end" : "center",
          gap: 8,
          padding: hasPreview ? "0 16px 16px" : "22px 16px",
          background: hasPreview ? `linear-gradient(to bottom, transparent 0%, ${REPORT_COLORS.cardBg} 75%)` : "transparent",
          border: "none",
          cursor: "pointer",
          textDecoration: "none",
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            backgroundColor: REPORT_COLORS.accent,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          <Unlock size={13} /> {ctaLabel}
        </span>
        {subtitle && (
          <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, textAlign: "center", maxWidth: 340, lineHeight: 1.4 }}>
            {subtitle}
          </span>
        )}
      </Wrapper>
    </div>
  )
}

// Faded placeholder cards for GRID/FILMSTRIP layouts (Top Competitors, Ad
// Creative, Competitor Threats) — sits alongside the real item cards, sized
// and shaped to match them via the caller's own renderPlaceholder, so a
// locked slice of the grid reads the same way every other locked section
// does: a faded preview of what's there. Deliberately has NO click target or
// CTA of its own (unlike the old per-tile "+N more / Unlock" chip this
// replaces) — the caller renders exactly ONE shared "Unlock full estimates"
// button below the whole grid (via BlurredContentFade), however many
// placeholder cards this renders, so the section never offers more than one
// way to unlock it.
export function BlurredGridPreview({ count, renderPlaceholder, height, width }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            filter: `blur(${3 + i * 1.5}px)`,
            opacity: Math.max(0.35, 0.9 - i * 0.12),
            pointerEvents: "none",
            userSelect: "none",
            height,
            minHeight: height,
            width,
            minWidth: width,
            flexShrink: 0,
          }}
        >
          {renderPlaceholder(i)}
        </div>
      ))}
    </>
  )
}

// Stand-in for an entire locked section, card, or list — a soft glass-panel
// card with the admin-set message and an upgrade CTA. Pass hideCta when a
// sibling section right next to this one already shows the one shared
// Unlock for both (e.g. paired Download/Revenue estimate cards) — this one
// then shows only the locked message, not a second redundant button.
export function BlurredBlock({ field, icon: Icon = Lock, title, hideCta = false }) {
  const { openUpgradeModal } = useContext(BlurUpgradeContext)
  const [hovered, setHovered] = useState(false)
  const label = title || (field && field.label) || null
  const message = (field && field.message) || "This part of the report is not available in the shared view."
  const ctaStyle = {
    display: "inline-block",
    padding: "8px 18px",
    borderRadius: 8,
    backgroundColor: REPORT_COLORS.accent,
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: hovered ? "0 4px 14px rgba(246,37,90,0.3)" : "none",
    transform: hovered ? "translateY(-1px)" : "none",
    transition: "box-shadow 0.18s ease, transform 0.18s ease",
  }
  const ctaHoverProps = { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }

  return (
    <div
      style={{
        padding: "32px 20px",
        textAlign: "center",
        borderRadius: REPORT_RADIUS,
        background: `linear-gradient(160deg, ${REPORT_COLORS.accentSoft}, rgba(255,255,255,0.02))`,
        border: `1px solid rgba(246,37,90,0.2)`,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: REPORT_COLORS.accentSoft,
          margin: "0 auto 12px",
        }}
      >
        <Icon size={18} color={REPORT_COLORS.accent} />
      </div>
      {label && <div style={{ color: REPORT_COLORS.textPrimary, fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{label} is locked</div>}
      <div style={{ color: REPORT_COLORS.textMuted, fontSize: 12, marginBottom: 14, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
        {message}
      </div>
      {!hideCta && (
        // Always the shared upgrade modal — see the matching comment in
        // BlurredInline above.
        <button type="button" onClick={openUpgradeModal} style={ctaStyle} {...ctaHoverProps}>
          Upgrade to Unlock
        </button>
      )}
    </div>
  )
}

// Coerces any raw field value to a safe display node: passes normal values
// straight through (or to `format` if given), swaps a blurred descriptor for
// the inline lock chip. Use this for scalars — numbers, short strings, badge
// labels — that don't already go through a shared formatter. Pass
// size="lg" when `value` would normally be a sentence/paragraph (a
// description, summary, or write-up) so the placeholder's footprint matches.
// Pass interactive=false when this field sits inside a section that already
// shows its own consolidated unlock CTA elsewhere, so this one renders as
// plain blurred text instead of a second (redundant) clickable Unlock.
export const blurAware = (value, format, size, interactive = true) => {
  if (isBlurredField(value)) return <BlurredInline field={value} size={size} interactive={interactive} />
  return format ? format(value) : value
}

// For a value that's normally wrapped in its own pill-shaped presentation —
// a ReportBadge, a tag chip — rendering a blurred value straight through
// `blurAware` would nest one pill inside another. Branch before wrapping
// instead: a locked value renders as its own bare pill, never inside the
// caller's badge. See blurAware above re: interactive.
export const badgeAware = (value, render, size, interactive = true) => (
  isBlurredField(value) ? <BlurredInline field={value} size={size} interactive={interactive} /> : render(value)
)

// Safe string coercion for helpers that derive a badge tone/color from a
// value (e.g. `.toLowerCase()`) — a blurred descriptor must never reach
// those, so treat anything non-string as empty rather than crashing.
export const safeLower = value => (typeof value === "string" ? value.toLowerCase() : "")
